import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {APPROVAL_TYPE} from '../../../constants';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import Util from '../../../forecast-app/shared/util/util';
import UpdateApprovalStatusMutation from '../../../mutations/UpdateApprovalStatusMutation';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {FlexColumn, Modal, Text, DatePicker} from '@forecast-it/design-system';
import moment from 'moment';

const SubmitTimeApprovalModal = ({
	selectedPersonId,
	selectedPersonName,
	currentViewingDate,
	usingTimeOffApproval,
	lockedDate,
	closeModal,
}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const startDateLimit = lockedDate && lockedDate.clone().add(1, 'd');
	const currentDateNoLock =
		lockedDate && lockedDate.isSameOrAfter(currentViewingDate, 'day') ? startDateLimit : currentViewingDate;

	const [selectedDate, setSelectedDate] = useState(currentDateNoLock);

	const handleConfirmButton = () => {
		trackEvent('Submit Timesheet Modal Save Button', 'Clicked');
		const onSuccess = res => {
			const personRes = res.updateApprovalStatus?.persons?.[0];
			if (personRes) {
				const newPersonLockDate = Util.CreateNonUtcMomentDate(
					personRes.submitLockedDateYear,
					personRes.submitLockedDateMonth,
					personRes.submitLockedDateDay
				);
				createToast({
					duration: 2500,
					message: `Timesheets submitted until ${newPersonLockDate.clone().format('LL')}`,
				});
				closeModal();
			} else {
				Util.checkForSageErrorAndShowModal(res.updateApprovalStatus.errors, false);
			}
		};

		// Mutation
		Util.CommitMutation(
			UpdateApprovalStatusMutation,
			{
				personIds: [selectedPersonId],
				approvalType: APPROVAL_TYPE.SUBMIT,
				endYear: selectedDate.year(),
				endMonth: selectedDate.month() + 1,
				endDay: selectedDate.date(),
				includeTimeOff: true,
			},
			onSuccess
		);
	};

	const cancelButton = () => {
		trackEvent('Submit Timesheet Modal Cancel Button', 'Clicked');
		closeModal();
	};

	const handleDateRangeChanged = newDate => {
		trackEvent('Submit Timesheet Modal Submit Until Date', 'Changed', {
			oldDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : 'No date',
			newDate: newDate ? newDate : 'No date',
		});
		setSelectedDate(newDate ? moment(newDate) : undefined);
	};

	const messageIdSuffix = (usingTimeOffApproval ? '_and_time_off' : '') + '_info';

	// When cleaning up this FF, also clean up the use of Moment in this component.

	const content = (
		<FlexColumn gap="m">
			<Text>
				{selectedPersonName
					? formatMessage({id: 'time_approval.submit_names_timesheets' + messageIdSuffix}, {name: selectedPersonName})
					: formatMessage({id: 'time_approval.submit_timesheets' + messageIdSuffix})}
			</Text>
			<DatePicker
				label={formatMessage({id: 'time_approval.submit_timesheets_until'})}
				onChange={handleDateRangeChanged.bind(this)}
				minValue={startDateLimit?.format('YYYY-MM-DD')}
				value={selectedDate?.format('YYYY-MM-DD')}
			/>
		</FlexColumn>
	);

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal}>
			<Modal.Title>
				{selectedPersonName
					? formatMessage({id: 'time_approval.submit_names_timesheets'}, {name: selectedPersonName})
					: formatMessage({id: 'time_approval.submit_timesheets'})}
			</Modal.Title>
			<Modal.Content>{content}</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={handleConfirmButton.bind(this)}
					dataCy="submit-time-save-button"
					disabled={!selectedDate}
				>
					{formatMessage({id: 'common.save'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={cancelButton.bind(this)}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

SubmitTimeApprovalModal.propTypes = {
	currentViewingDate: PropTypes.object,
	closeModal: PropTypes.func.isRequired,
};

export default SubmitTimeApprovalModal;
